import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../App';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import NotificationBell from '../components/NotificationBell';
import {
  Sparkles, Headphones, BookOpen, PenTool, Mic,
  ArrowLeft, ArrowRight, BarChart3, Trophy, Zap, LogOut, User,
  CheckCircle, Clock, Lock, Target, TrendingUp,
  AlertTriangle, Timer, RefreshCw, Users, ShieldAlert, CreditCard,
  Calendar, Pause, Play, Flame
} from 'lucide-react';

// === ACCESS COUNTDOWN BANNER ===
const AccessCountdown = ({ accessInfo, level, onExtend }) => {
  if (!accessInfo?.purchased || !accessInfo.expires_at) return null;

  const { days_remaining, total_days, time_percent_remaining, active, expired } = accessInfo;

  if (expired) return null; // Handled by the overlay

  // Color logic: green >60%, orange 30-60%, red <30%
  const getColor = (pct) => {
    if (pct > 60) return { bg: 'bg-emerald-500', track: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', light: 'bg-emerald-50' };
    if (pct > 30) return { bg: 'bg-amber-500', track: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', light: 'bg-amber-50' };
    return { bg: 'bg-red-500', track: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', light: 'bg-red-50' };
  };

  const colors = getColor(time_percent_remaining);
  const timeUsedPct = Math.min(100, Math.max(0, 100 - time_percent_remaining));

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border ${colors.border} ${colors.light} p-5 mb-6`}
      data-testid="access-countdown-banner"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${colors.track} flex items-center justify-center`}>
            <Timer className={`w-5 h-5 ${colors.text}`} />
          </div>
          <div>
            <p className={`font-semibold text-sm ${colors.text}`}>
              {days_remaining} day{days_remaining !== 1 ? 's' : ''} left to complete your DELF {level} program
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {accessInfo.days_used} of {total_days} days used
            </p>
          </div>
        </div>
        {days_remaining <= 7 && (
          <Button
            size="sm"
            variant="outline"
            className={`rounded-full border ${colors.border} ${colors.text} hover:${colors.light} shrink-0`}
            onClick={onExtend}
            data-testid="extend-access-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Extend Access
          </Button>
        )}
      </div>

      {/* Time Progress Bar */}
      <div className="mt-4">
        <div className={`h-2.5 rounded-full ${colors.track} overflow-hidden`}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${timeUsedPct}%` }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className={`h-full rounded-full ${colors.bg}`}
          />
        </div>
        <div className="flex justify-between mt-1.5 text-xs text-muted-foreground">
          <span>Started</span>
          <span className={`font-medium ${colors.text}`}>{time_percent_remaining}% time remaining</span>
          <span>Expires</span>
        </div>
      </div>
    </motion.div>
  );
};

// === WARNING BANNER ===
const WarningBanner = ({ daysRemaining, level }) => {
  if (!daysRemaining || daysRemaining > 7) return null;

  const config = daysRemaining <= 1
    ? { icon: ShieldAlert, bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800', msg: `Urgent: Your DELF ${level} access expires tomorrow! Complete your exercises now.` }
    : daysRemaining <= 3
    ? { icon: AlertTriangle, bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', msg: `Only ${daysRemaining} days left on your DELF ${level} access. Don't lose your progress!` }
    : { icon: AlertTriangle, bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', msg: `${daysRemaining} days remaining on your DELF ${level} program. Keep studying!` };

  const Icon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`flex items-center gap-3 px-5 py-3 rounded-xl border ${config.border} ${config.bg} mb-6`}
      data-testid="warning-banner"
    >
      <Icon className={`w-5 h-5 shrink-0 ${config.text}`} />
      <p className={`text-sm font-medium ${config.text}`}>{config.msg}</p>
    </motion.div>
  );
};

// === EXPIRED OVERLAY ===

// === EXAM COUNTDOWN CARD ===
const ExamCountdownCard = ({ examProfile, level, onPause, onResume, pauseLoading }) => {
  if (!examProfile?.has_exam_date) return null;

  const { days_until_exam, overall_progress, modules, is_paused, pause_used, pause_days_left } = examProfile;
  const examDate = new Date(examProfile.exam_date);

  const moduleList = [
    { key: 'comprehension_orale', label: 'Listening', icon: Headphones, color: 'text-blue-500' },
    { key: 'comprehension_ecrite', label: 'Reading', icon: BookOpen, color: 'text-green-500' },
    { key: 'production_ecrite', label: 'Writing', icon: PenTool, color: 'text-purple-500' },
    { key: 'production_orale', label: 'Speaking', icon: Mic, color: 'text-orange-500' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-border shadow-sm p-6 mb-6"
      data-testid="exam-countdown-card"
    >
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
        {/* Left: Countdown */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-primary" />
            <span className="text-xs font-semibold text-primary uppercase tracking-wider">Your DELF Exam</span>
          </div>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-5xl font-bold tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }} data-testid="days-until-exam">
              {days_until_exam}
            </span>
            <span className="text-lg font-medium text-muted-foreground">days remaining</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Exam: {examDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </p>

          {is_paused && (
            <div className="mt-3 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5" data-testid="pause-active-badge">
              <Pause className="w-3.5 h-3.5 text-amber-600" />
              <span className="text-xs font-semibold text-amber-700">Paused — {pause_days_left} days left</span>
            </div>
          )}
        </div>

        {/* Center: Today's Goals */}
        <div className="flex-1 border-l border-border/50 pl-6 hidden md:block">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Today's Goal</p>
          <div className="space-y-2">
            {moduleList.map(mod => {
              const progress = modules[mod.key];
              const done = progress && progress.pct >= 100;
              return (
                <div key={mod.key} className="flex items-center gap-2 text-sm">
                  {done ? <CheckCircle className="w-4 h-4 text-green-500" /> : <mod.icon className={`w-4 h-4 ${mod.color}`} />}
                  <span className={done ? 'line-through text-muted-foreground' : 'font-medium'}>{mod.label}</span>
                  {progress && <span className="text-xs text-muted-foreground ml-auto">{progress.pct}%</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Overall Progress */}
        <div className="flex-shrink-0 text-center">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Overall Preparation</p>
          <div className="relative w-20 h-20 mx-auto">
            <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="34" fill="none" stroke="#e5e7eb" strokeWidth="6" />
              <circle cx="40" cy="40" r="34" fill="none" stroke="hsl(var(--primary))" strokeWidth="6"
                strokeDasharray={`${(overall_progress / 100) * 213.6} 213.6`}
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-lg font-bold" data-testid="overall-progress-pct">
              {overall_progress}%
            </span>
          </div>
        </div>
      </div>

      {/* Pause / Resume */}
      <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {!pause_used ? 'You have 1 free 14-day pause available' : (is_paused ? 'Pause active' : 'Free pause used')}
        </p>
        {!pause_used && !is_paused && (
          <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={onPause} disabled={pauseLoading} data-testid="activate-pause-btn">
            <Pause className="w-3 h-3 mr-1" /> Pause Countdown
          </Button>
        )}
        {is_paused && (
          <Button variant="outline" size="sm" className="rounded-full text-xs" onClick={onResume} disabled={pauseLoading} data-testid="resume-btn">
            <Play className="w-3 h-3 mr-1" /> Resume Now
          </Button>
        )}
      </div>
    </motion.div>
  );
};

// === EXTENSION OFFER (shows when ≤14 days remaining) ===
const ExtensionOffer = ({ level, daysRemaining, onExtend }) => {
  if (!daysRemaining || daysRemaining > 14) return null;

  const prices = { A2: 29, B1: 39, B2: 49 };
  const price = prices[level] || 29;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-6 mb-6"
      data-testid="extension-offer"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
          <Flame className="w-6 h-6 text-amber-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-lg mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>Need more preparation time?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Only {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left. Extend your access to keep preparing.
          </p>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">+30 Days</p>
              <p className="text-3xl font-bold text-amber-700" data-testid="extension-price">${price}</p>
            </div>
            <Button
              className="rounded-full px-6 py-5 font-bold bg-amber-500 hover:bg-amber-600 text-white border-0"
              onClick={() => onExtend('extend_30')}
              data-testid="extend-my-access-btn"
            >
              Extend My Access
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// === EXPIRED OVERLAY ===
const ExpiredOverlay = ({ level, navigate }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="fixed inset-0 z-[100] bg-[#FDFCF8]/95 backdrop-blur-sm flex items-center justify-center p-6"
    data-testid="expired-overlay"
  >
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay: 0.1, type: 'spring', damping: 20 }}
      className="bg-white rounded-3xl border border-border shadow-2xl max-w-lg w-full p-8 text-center"
    >
      <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-6">
        <Lock className="w-8 h-8 text-red-500" />
      </div>

      <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Syne, sans-serif' }} data-testid="expired-title">
        Your DELF {level} Access Has Expired
      </h1>
      <p className="text-muted-foreground mb-8 max-w-sm mx-auto">
        Your study period has ended. Extend your access to continue where you left off, or join the Success Club for ongoing practice.
      </p>

      <div className="space-y-3 mb-6">
        <Button
          className="w-full rounded-full py-5 font-bold"
          onClick={() => navigate('/pricing')}
          data-testid="extend-access-cta-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Extend Access (+30 days from $49)
        </Button>
        <Button
          variant="outline"
          className="w-full rounded-full py-5 font-semibold border-primary/30 text-primary"
          onClick={() => navigate('/pricing')}
          data-testid="join-club-cta-btn"
        >
          <Users className="w-4 h-4 mr-2" />
          Join DELF Success Club — $29/mo
        </Button>
      </div>

      <button
        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => navigate('/levels')}
        data-testid="back-to-levels-expired-btn"
      >
        Back to all levels
      </button>
    </motion.div>
  </motion.div>
);

// === MAIN DASHBOARD ===
const LevelDashboard = () => {
  const { level } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [examProfile, setExamProfile] = useState(null);
  const [pauseLoading, setPauseLoading] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchExamProfile();
  }, [level]);

  const fetchDashboard = async () => {
    try {
      const response = await api.get(`/levels/${level}`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard');
      navigate('/levels');
    } finally {
      setLoading(false);
    }
  };

  const fetchExamProfile = async () => {
    try {
      const response = await api.get(`/payments/exam-profile/${level}`);
      setExamProfile(response.data);
    } catch (e) {}
  };

  const handlePause = async () => {
    setPauseLoading(true);
    try {
      await api.post('/payments/pause-access', { level });
      toast.success('Countdown paused for 14 days');
      fetchExamProfile();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pause');
    } finally {
      setPauseLoading(false);
    }
  };

  const handleResume = async () => {
    setPauseLoading(true);
    try {
      await api.post('/payments/resume-access', { level });
      toast.success('Countdown resumed');
      fetchExamProfile();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume');
    } finally {
      setPauseLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const moduleConfig = {
    comprehension_orale: {
      icon: Headphones,
      color: "bg-blue-500",
      bgLight: "bg-blue-50",
      textColor: "text-blue-600",
      gradient: "from-blue-400 to-blue-600"
    },
    comprehension_ecrite: {
      icon: BookOpen,
      color: "bg-green-500",
      bgLight: "bg-green-50",
      textColor: "text-green-600",
      gradient: "from-green-400 to-green-600"
    },
    production_ecrite: {
      icon: PenTool,
      color: "bg-purple-500",
      bgLight: "bg-purple-50",
      textColor: "text-purple-600",
      gradient: "from-purple-400 to-purple-600"
    },
    production_orale: {
      icon: Mic,
      color: "bg-orange-500",
      bgLight: "bg-orange-50",
      textColor: "text-orange-600",
      gradient: "from-orange-400 to-orange-600"
    }
  };

  const levelColors = {
    A2: { gradient: "from-green-400 to-green-600", text: "text-green-600", bg: "bg-green-500" },
    B1: { gradient: "from-blue-400 to-blue-600", text: "text-blue-600", bg: "bg-blue-500" },
    B2: { gradient: "from-purple-400 to-purple-600", text: "text-purple-600", bg: "bg-purple-500" }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress': return <Clock className="w-5 h-5 text-orange-500" />;
      default: return <Lock className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return { text: 'Completed', color: 'bg-green-100 text-green-700' };
      case 'in_progress': return { text: 'In Progress', color: 'bg-orange-100 text-orange-700' };
      default: return { text: 'Not Started', color: 'bg-gray-100 text-gray-600' };
    }
  };

  const getReadinessColor = (score) => {
    if (score >= 85) return 'text-green-500';
    if (score >= 70) return 'text-blue-500';
    if (score >= 50) return 'text-orange-500';
    return 'text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  const totalProgress = dashboard?.total_progress || 0;
  const readinessScore = dashboard?.readiness_score || 0;
  const accessInfo = dashboard?.access_info;
  const isExpired = accessInfo?.expired === true;
  const daysRemaining = accessInfo?.days_remaining;

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Expired Overlay */}
      <AnimatePresence>
        {isExpired && <ExpiredOverlay level={level} navigate={navigate} />}
      </AnimatePresence>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFCF8]/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/levels" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
                DELF Success Academy
              </span>
            </Link>
            <span className={`px-3 py-1 rounded-full text-sm font-bold text-white bg-gradient-to-r ${levelColors[level]?.gradient}`}>
              {level}
            </span>
          </div>

          <div className="flex items-center gap-4">
            <Link to="/daily">
              <Button variant="ghost" className="gap-2" data-testid="daily-practice-btn">
                <Zap className="w-4 h-4" />
                <span className="hidden sm:inline">Daily Practice</span>
              </Button>
            </Link>
            <Link to="/progress">
              <Button variant="ghost" className="gap-2" data-testid="progress-btn">
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">Progress</span>
              </Button>
            </Link>
            <NotificationBell />
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
              ) : (
                <User className="w-4 h-4 text-muted-foreground" />
              )}
              <span className="text-sm font-medium hidden sm:inline">{user?.name?.split(' ')[0]}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Level Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center gap-2 mb-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/levels')}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              All Levels
            </Button>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
            Réussir DELF {level}
          </h1>
          <p className="text-base text-muted-foreground mt-2">
            Complete all 4 modules to be fully prepared for your exam
          </p>
        </motion.div>

        {/* === ACCESS COUNTDOWN === */}
        <AccessCountdown
          accessInfo={accessInfo}
          level={level}
          onExtend={() => navigate('/pricing')}
        />

        {/* === WARNING BANNER === */}
        <WarningBanner daysRemaining={daysRemaining} level={level} />

        {/* === EXAM COUNTDOWN CARD === */}
        <ExamCountdownCard
          examProfile={examProfile}
          level={level}
          onPause={handlePause}
          onResume={handleResume}
          pauseLoading={pauseLoading}
        />

        {/* === EXTENSION OFFER (≤14 days) === */}
        <ExtensionOffer
          level={level}
          daysRemaining={daysRemaining}
          onExtend={(planId) => navigate('/pricing')}
        />

        {/* Main Stats Row */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Overall Progress Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`bg-gradient-to-r ${levelColors[level]?.gradient} rounded-2xl p-6 text-white md:col-span-2`}
          >
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Trophy className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Overall Progress</h2>
                  <p className="text-white/80">
                    {dashboard?.total_completed || 0} of {dashboard?.total_exercises || 40} exercises completed
                  </p>
                </div>
              </div>
              <div className="text-center md:text-right w-full md:w-auto">
                <div className="text-5xl font-bold">{Math.round(totalProgress)}%</div>
                <Progress value={totalProgress} className="w-full md:w-48 h-3 bg-white/30 mt-2" />
              </div>
            </div>
          </motion.div>

          {/* Readiness Score Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-bold">DELF Readiness</h3>
                <p className="text-xs text-muted-foreground">Your exam preparation score</p>
              </div>
            </div>

            <div className="text-center mb-4">
              <div className={`text-5xl font-bold ${getReadinessColor(readinessScore)}`}>
                {readinessScore}%
              </div>
            </div>

            <div className="relative pt-1">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>0%</span>
                <span>Ready!</span>
              </div>
              <div className="h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    readinessScore >= 85 ? 'bg-green-500' :
                    readinessScore >= 70 ? 'bg-blue-500' :
                    readinessScore >= 50 ? 'bg-orange-500' : 'bg-muted-foreground'
                  }`}
                  style={{ width: `${readinessScore}%` }}
                />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Motivation Message */}
        {dashboard?.readiness_message && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`rounded-xl p-4 mb-8 flex items-center gap-4 ${
              readinessScore >= 85 ? 'bg-green-50 border border-green-200' :
              readinessScore >= 70 ? 'bg-blue-50 border border-blue-200' :
              readinessScore >= 50 ? 'bg-orange-50 border border-orange-200' :
              'bg-muted/50 border border-border'
            }`}
          >
            <TrendingUp className={`w-6 h-6 ${
              readinessScore >= 85 ? 'text-green-600' :
              readinessScore >= 70 ? 'text-blue-600' :
              readinessScore >= 50 ? 'text-orange-600' :
              'text-muted-foreground'
            }`} />
            <div>
              <p className="font-medium">{dashboard.readiness_message}</p>
              <p className="text-sm text-muted-foreground">{dashboard.readiness_message_fr}</p>
            </div>
          </motion.div>
        )}

        {/* Modules Grid */}
        <h2 className="text-2xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
          Your Modules
        </h2>

        <div className="grid md:grid-cols-2 gap-6">
          {dashboard?.modules?.map((module, index) => {
            const config = moduleConfig[module.module];
            const Icon = config?.icon || BookOpen;
            const statusInfo = getStatusLabel(module.status);

            return (
              <motion.div
                key={module.module}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * (index + 1) }}
                className="bg-white rounded-2xl border border-border/50 overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
                onClick={() => navigate(`/level/${level}/${module.module}`)}
                data-testid={`module-${module.module}`}
              >
                {/* Progress Bar at Top */}
                <div className="h-2 bg-muted">
                  <div
                    className={`h-full bg-gradient-to-r ${config?.gradient} transition-all duration-500`}
                    style={{ width: `${module.progress_percent}%` }}
                  />
                </div>

                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl ${config?.color} flex items-center justify-center`}>
                        <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                          {module.name_fr}
                        </h3>
                        <p className="text-sm text-muted-foreground">{module.name}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {getStatusIcon(module.status)}
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusInfo.color}`}>
                        {statusInfo.text}
                      </span>
                    </div>
                  </div>

                  {/* Progress Section */}
                  <div className="bg-muted/30 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-muted-foreground">Module Progress</span>
                      <span className={`text-lg font-bold ${config?.textColor}`}>
                        {module.progress_percent}%
                      </span>
                    </div>
                    <Progress value={module.progress_percent} className="h-2" />
                    <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                      <span>{module.completed_count}/{module.exercises_count} exercises</span>
                      {module.average_score > 0 && (
                        <span className="font-medium">Avg: {module.average_score}/25</span>
                      )}
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="text-center p-2 bg-green-50 rounded-lg">
                      <div className="text-lg font-bold text-green-600">{module.completed_count}</div>
                      <div className="text-xs text-green-600">Completed</div>
                    </div>
                    <div className="text-center p-2 bg-blue-50 rounded-lg">
                      <div className="text-lg font-bold text-blue-600">{module.unlocked_count}</div>
                      <div className="text-xs text-blue-600">Unlocked</div>
                    </div>
                    <div className="text-center p-2 bg-muted rounded-lg">
                      <div className="text-lg font-bold text-muted-foreground">
                        {module.exercises_count - module.unlocked_count}
                      </div>
                      <div className="text-xs text-muted-foreground">Locked</div>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className={`flex items-center justify-between px-4 py-3 rounded-xl ${config?.bgLight}`}>
                    <span className={`text-sm font-medium ${config?.textColor}`}>
                      {module.status === 'completed' ? 'Review Exercises' :
                       module.status === 'in_progress' ? 'Continue Learning' : 'Start Module'}
                    </span>
                    <ArrowRight className={`w-5 h-5 ${config?.textColor}`} />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 grid md:grid-cols-2 gap-4"
        >
          <Link to="/daily" className="block">
            <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl p-6 border border-primary/20 hover:border-primary/40 transition-colors">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold">Daily Practice</h3>
                  <p className="text-sm text-muted-foreground">5-minute exercises to build your streak</p>
                </div>
              </div>
            </div>
          </Link>

          <Link to="/pricing" className="block">
            <div className="bg-muted/50 rounded-xl p-6 border border-border/50 hover:border-primary/20 transition-colors">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-bold">Manage Access</h3>
                  <p className="text-sm text-muted-foreground">View plans, extend access, or subscribe</p>
                </div>
              </div>
            </div>
          </Link>
        </motion.div>
      </main>
    </div>
  );
};

export default LevelDashboard;
